import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cameron-portfolio/artifacts/qpotgkzg_Screenshot%202026-05-11%20at%204.26.19%E2%80%AFPM.png";

const BELIEFS = [
  {
    no: "I",
    title: "Dreams aren't small.",
    body: "Beliefs are.",
  },
  {
    no: "II",
    title: "Better doesn't win.",
    body: "Different does.",
  },
  {
    no: "III",
    title: "Mistakes don't exist.",
    body: "There's only opportunity.",
  },
];

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};
const item = {
  hidden: { y: 24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 110, damping: 16 },
  },
};

export default function Belief() {
  return (
    <section
      id="belief"
      data-testid="belief-section"
      className="relative bg-black text-white overflow-hidden border-t border-white/10"
    >
      {/* Background portrait — anchored bottom-left, holographic grey */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        data-testid="belief-bg-portrait"
      >
        <img
          src={PORTRAIT}
          alt=""
          draggable={false}
          className="absolute bottom-0 left-0 h-full w-auto max-w-[55%] object-cover object-bottom select-none"
          style={{
            filter:
              "grayscale(1) contrast(1.05) brightness(0.45) hue-rotate(195deg)",
            opacity: 0.55,
          }}
        />
        {/* Holographic blue tint */}
        <div
          className="absolute bottom-0 left-0 h-full w-full max-w-[55%]"
          style={{
            background:
              "linear-gradient(135deg, rgba(220,38,38,0.20) 0%, rgba(147,197,253,0.06) 45%, transparent 80%)",
            mixBlendMode: "screen",
          }}
        />
        {/* Iridescent scanlines */}
        <div
          className="absolute bottom-0 left-0 h-full w-full max-w-[55%] opacity-25"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)",
            mixBlendMode: "overlay",
          }}
        />
        {/* Fade-to-black on right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.4) 22%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.95) 78%, #000 100%)",
          }}
        />
        {/* Soft blue radial wash bottom-right */}
        <div
          className="absolute -bottom-32 -right-32 w-[50vw] h-[50vw] opacity-40"
          style={{
            background:
              "radial-gradient(circle at center, rgba(220,38,38,0.18) 0%, rgba(127,29,29,0.04) 40%, transparent 70%)",
          }}
        />
      </div>

      {/* Edition label, vertical right edge */}
      <div
        aria-hidden
        className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 -rotate-90 origin-center font-mono text-[10px] uppercase tracking-[0.45em] text-white/25 select-none"
      >
        Note · I of II · MMXXVI
      </div>

      <motion.div
        className="relative mx-auto max-w-[1760px] px-6 md:px-12 lg:px-16 py-28 md:py-36 lg:py-44 grid grid-cols-1 lg:grid-cols-12 gap-10"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={stagger}
      >
        {/* Left spacer for portrait */}
        <div className="hidden lg:block lg:col-span-4" aria-hidden />

        {/* Quote block + beliefs */}
        <div className="lg:col-span-8 max-w-[860px]">
          {/* Header strip */}
          <motion.div
            className="flex items-center gap-4 mb-10"
            variants={item}
          >
            <span
              className="font-display font-black tracking-tighter text-3xl md:text-4xl"
              style={{
                background:
                  "linear-gradient(180deg, rgba(147,197,253,0.85) 0%, rgba(220,38,38,0.45) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              I
            </span>
            <span className="h-px flex-1 max-w-[80px] bg-white/25" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.32em] text-white/65">
              Personal Note · 01 / 02
            </span>
          </motion.div>

          {/* Giant pull-quote with hung opening mark */}
          <motion.div className="relative" variants={item}>
            <span
              aria-hidden
              className="absolute -top-8 md:-top-12 -left-2 md:-left-6 font-script italic font-normal select-none pointer-events-none leading-[1] text-[6rem] md:text-[10rem] lg:text-[12rem]"
              style={{
                background:
                  "linear-gradient(180deg, rgba(147,197,253,0.6) 0%, rgba(220,38,38,0.15) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              “
            </span>
            <h2
              className="relative font-display font-black tracking-tighter leading-[0.92] text-4xl md:text-6xl lg:text-7xl text-white"
              data-testid="belief-quote"
            >
              No{" "}
              <span className="text-white/35 line-through decoration-[3px] decoration-[#DC2626]/70">
                small
              </span>{" "}
              <span
                className="font-script italic font-normal whitespace-nowrap"
                style={{
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 65%, #7F1D1D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  paddingBottom: "0.18em",
                  paddingRight: "0.05em",
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                dreams —
              </span>
              <br className="hidden md:block" />
              only small beliefs.
            </h2>
          </motion.div>

          {/* Attribution / signature flourish */}
          <motion.div
            className="mt-8 flex items-center gap-4"
            variants={item}
            data-testid="belief-signature"
          >
            <span
              aria-hidden
              className="h-px w-12 bg-white/30"
            />
            <span
              className="font-script italic text-2xl md:text-3xl"
              style={{
                background:
                  "linear-gradient(180deg, #ffffff 0%, #FCA5A5 60%, #DC2626 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                lineHeight: 1.1,
              }}
            >
              — Cameron Bergsma
            </span>
          </motion.div>

          {/* Short bio */}
          <motion.p
            className="mt-10 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
            variants={item}
            data-testid="belief-body"
          >
            He works inside <em className="text-white not-italic font-medium">the gap</em>{" "}
            — the quiet distance between where someone stands and what they
            could become.
          </motion.p>

          {/* Manifesto */}
          <motion.div
            className="mt-14 border-t border-white/10"
            variants={item}
            data-testid="belief-tenets"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {BELIEFS.map((b) => (
                <div
                  key={b.no}
                  data-testid={`belief-tenet-${b.no}`}
                  className="group relative py-7 md:py-8 md:px-7 first:md:pl-0 last:md:pr-0"
                >
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#FCA5A5] mb-3 block"
                  >
                    Belief · {b.no}
                  </span>
                  <h4 className="font-display font-black tracking-tight text-xl md:text-2xl text-white leading-[1.05]">
                    {b.title}
                  </h4>
                  <p className="mt-3 text-sm md:text-base text-white/55 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              ))}
            </div>
            <div className="border-b border-white/10" />
          </motion.div>

          {/* CTAs */}
          <motion.div
            className="mt-12 flex flex-wrap items-center gap-4"
            variants={item}
          >
            <a
              href="/about"
              data-testid="belief-cta-about"
              className="cta-clay-blue group inline-flex items-center gap-2 h-12 px-7 rounded-full text-xs font-mono font-semibold uppercase tracking-[0.2em] text-white"
            >
              <span className="relative z-10">About Cameron</span>
              <ArrowRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="/publications"
              data-testid="belief-cta-book"
              className="group inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.22em] text-white/75 hover:text-white transition-colors"
            >
              <span className="relative">
                Read the book
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-0 -bottom-1 h-px w-full bg-white/40 group-hover:bg-[#DC2626] transition-colors"
                />
              </span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
